/**
 * Componente SubidaXML
 * Permite cargar y parsear archivos XML de facturas
 */

import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import axios from 'axios';

const API_BASE_URL = (process.env.REACT_APP_API_URL || 'http://localhost:3002') + '/api';

const SubidaXML = ({ onXMLParsed, loading }) => {
  const [archivo, setArchivo] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState(null);
  const [datosParseados, setDatosParseados] = useState(null);

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const empresaId = user.empresaId || '';
  const token = localStorage.getItem('token');

  const handleArchivoSeleccionado = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      if (!file.name.endsWith('.xml')) {
        setError('Por favor selecciona un archivo XML válido');
        return;
      }
      setArchivo(file);
      setError(null);
    }
  };

  const handleParsearXML = async () => {
    if (!archivo) {
      setError('Por favor selecciona un archivo XML');
      return;
    }

    setCargando(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('xml', archivo);

      const response = await axios.post(
        `${API_BASE_URL}/contabilidad/compras/parsear-xml`,
        formData,
        {
          headers: {
            'empresa-id': empresaId,
            'Content-Type': 'multipart/form-data',
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      const datos = response.data;
      setDatosParseados(datos);

      // Estructura del backend: { factura: { proveedor:{}, factura:{}, productos:[], totales:{} } }
      const facturaWrapper = datos.factura || {};
      const facturaInfo = facturaWrapper.factura || {};
      const proveedorInfo = facturaWrapper.proveedor || {};
      const productosInfo = facturaWrapper.productos || [];

      // Construir número de comprobante
      const numComprobante = (facturaInfo.establecimiento && facturaInfo.puntoEmision && facturaInfo.secuencial)
        ? `${facturaInfo.establecimiento}-${facturaInfo.puntoEmision}-${facturaInfo.secuencial}`
        : facturaInfo.numeroComprobante || '';

      // Llamar callback con TODOS los datos del XML
      onXMLParsed({
        numero_factura: numComprobante,
        fecha_factura: facturaInfo.fechaEmision || new Date().toISOString().split('T')[0],
        codigo_autorizacion: facturaInfo.claveAcceso || '',
        proveedor_nombre: proveedorInfo.razonSocial || proveedorInfo.nombreComercial || '',
        proveedor_ruc: proveedorInfo.ruc || '',
        proveedor_direccion: proveedorInfo.direccion || '',
        items: productosInfo.map((p) => ({
          codigo: p.codigoPrincipal || p.codigo || '',
          descripcion: p.descripcion || p.nombre || '',
          cantidad: parseFloat(p.cantidad || 1),
          precio_unitario: parseFloat(p.precioUnitario || p.precio_unitario || p.precioTotalSinImpuesto || 0),
          subtotal: (parseFloat(p.cantidad || 1) * parseFloat(p.precioUnitario || p.precio_unitario || 0)).toFixed(2),
          producto_id: '',
          producto_nombre: '',
          deducible: false,
        })),
      });
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message;
      setError(`Error al parsear XML: ${errorMsg}`);
      setDatosParseados(null);
    } finally {
      setCargando(false);
    }
  };

  return (
    <Box sx={{ py: 3 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Paso 1: Subir Archivo XML
      </Typography>

      <Paper sx={{ p: 3, textAlign: 'center', border: '2px dashed #ccc' }}>
        <CloudUploadIcon sx={{ fontSize: 48, color: '#999', mb: 2 }} />

        <Typography variant="body1" sx={{ mb: 2 }}>
          Arrastra un archivo XML aquí o haz clic para seleccionar
        </Typography>

        <input
          type="file"
          accept=".xml"
          onChange={handleArchivoSeleccionado}
          style={{ display: 'none' }}
          id="xml-input"
        />

        <label htmlFor="xml-input">
          <Button
            variant="contained"
            component="span"
            disabled={cargando}
            sx={{ mb: 2 }}
          >
            Seleccionar Archivo
          </Button>
        </label>

        {archivo && (
          <Typography variant="body2" sx={{ mt: 2, color: 'green' }}>
            ✓ Archivo seleccionado: {archivo.name}
          </Typography>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        {datosParseados && (
          <Alert severity="success" sx={{ mt: 2 }}>
            ✓ XML parseado correctamente
          </Alert>
        )}

        <Button
          variant="contained"
          color="primary"
          onClick={handleParsearXML}
          disabled={!archivo || cargando}
          sx={{ mt: 2 }}
        >
          {cargando ? <CircularProgress size={24} /> : 'Parsear XML'}
        </Button>
      </Paper>

      {datosParseados && (
        <Paper sx={{ p: 3, mt: 3, bgcolor: '#f5f5f5' }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Datos Extraídos del XML
          </Typography>

          <List>
            <ListItem>
              <ListItemText
                primary="Número de Factura"
                secondary={datosParseados.numero_factura}
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Fecha de Factura"
                secondary={datosParseados.fecha_factura}
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Código de Autorización"
                secondary={datosParseados.codigo_autorizacion}
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Items"
                secondary={`${datosParseados.items?.length || 0} items encontrados`}
              />
            </ListItem>
          </List>
        </Paper>
      )}
    </Box>
  );
};

export default SubidaXML;
